import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import { getNews } from '../lib/api';
import Seo from '../components/Seo';

/**
 * Per-slug crop overrides for news cover images. Each entry is a CSS
 * object-position value (Y as a percentage from the top of the source image).
 * Default is `50% 0%` (object-top) which suits most tight headshots. Override
 * when a photo has extra headroom or a lower face position.
 */
const COVER_POSITION = {
  'joe-facer-joins-dbm': '50% 18%',
  'anna-louise-gregson-joins-dbm': '50% 22%',
  'luke-woodcock-joins-dbm': '50% 32%',
  'daisy-heron-joins-dbm': '50% 28%',
};
const coverPos = (slug) => COVER_POSITION[slug] || '50% 0%';

export default function News() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState('All');

  useEffect(() => { getNews().then(setItems).catch(() => {}); }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(items.map(i => i.category)))], [items]);
  const filtered = useMemo(() => cat === 'All' ? items : items.filter(i => i.category === cat), [items, cat]);
  const [lead, ...rest] = filtered;

  return (
    <div data-testid="news-page" className="bg-white">
      <Seo
        title="News"
        description="News, castings and career updates from across the David Ball Management client list."
        path="/news"
      />
      <section className="pt-40 pb-16 lg:pt-48 lg:pb-24 bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal><SectionEyebrow>News</SectionEyebrow></Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-10 items-end">
            <Reveal className="lg:col-span-8" delay={120}>
              <h1 className="font-heading uppercase text-[#002b5c] text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
                Notes from<br /><span className="italic font-light">the client list.</span>
              </h1>
            </Reveal>
            <Reveal className="lg:col-span-4" delay={240}>
              <p className="text-base text-[#4a4a4a] leading-relaxed">
                News, castings and career updates from across our client list.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-10">
        <div className="flex flex-wrap gap-2 border-y border-black/10 py-6">
          {categories.map((c) => (
            <button key={c}
              data-testid={`news-cat-${c}`}
              onClick={() => setCat(c)}
              className={`rounded-full px-4 py-2 text-[11px] tracking-[0.18em] uppercase transition-colors border ${
                cat === c ? 'bg-[#002b5c] text-white border-[#002b5c]' : 'border-black/10 text-[#333] hover:border-[#002b5c]'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {lead && (
        <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <Reveal>
            <Link to={`/news/${lead.slug}`} className="block group grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 img-hover rounded-2xl overflow-hidden aspect-[16/10] bg-[#faf8f4]">
                <img
                  src={typeof lead.cover === 'string' ? lead.cover.replace('/fill/w_900,h_1200,al_c,', '/fill/w_1600,h_1000,al_c,') : lead.cover}
                  alt=""
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="lg:col-span-5">
                <p className="eyebrow">{lead.category} · {new Date(lead.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase text-[#002b5c] mt-4 leading-[0.95] group-hover:text-[#C5A059] transition-colors">{lead.title}</h2>
                <p className="mt-6 text-[#4a4a4a] leading-relaxed">{lead.excerpt}</p>
                <span className="mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] gold-underline">
                  Read <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {rest.map((n, i) => (
            <Reveal key={n.id} delay={(i % 6) * 60}>
              <Link to={`/news/${n.slug}`} className="block group">
                <div className="img-hover rounded-2xl overflow-hidden aspect-[3/4] bg-[#faf8f4]">
                  <img src={n.cover} alt="" className="w-full h-full object-cover" style={{ objectPosition: coverPos(n.slug) }} />
                </div>
                <p className="eyebrow mt-6">{n.category} · {new Date(n.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                <h3 className="font-heading text-2xl uppercase text-[#002b5c] mt-2 leading-tight group-hover:text-[#C5A059] transition-colors">{n.title}</h3>
                <p className="mt-3 text-sm text-[#4a4a4a] leading-relaxed">{n.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
